/* header-mobile-search.js
   Contrôleur mobile unique pour la recherche du header et la toolbar des
   collections. Il sépare l'intention de scroll de l'éligibilité de la toolbar :
   la recherche peut se ranger dès le haut, tandis que la toolbar ne se range
   qu'une fois sa position naturelle dépassée. Au scroll up, les deux reviennent
   dans le même frame. La barre de catégories mobile utilise ce même rAF avec
   deux seuils stables, sans ajouter de second listener de scroll.

   Les dimensions sont mesurées uniquement à l'initialisation et aux vrais
   changements de largeur. Les animations sont confiées au CSS : aucune boucle
   requestAnimationFrame ne relit la mise en page pendant une transition.
*/
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768.98px)';
  var HIDE_DISTANCE = 28;
  var SHOW_DISTANCE = 14;
  var TOP_REVEAL = 80;
  var CATS_HIDE_AT = 72;
  var CATS_SHOW_AT = 24;
  var TRANSITION_MS = 220;

  function sectionContainsManagedControls(section) {
    if (!section || section.nodeType !== 1) return false;
    if (
      typeof section.matches === 'function'
      && section.matches('store-header, .cc-collection-products')
    ) return true;

    return Boolean(
      typeof section.querySelector === 'function'
      && section.querySelector(
        'store-header, .header__search, .dg-mobile-cats, '
        + '.cc-collection-products products-toolbar.products-toolbar'
      )
    );
  }

  function initMobileControls() {
    if (typeof window.__dgMobileControlsCleanup === 'function') {
      window.__dgMobileControlsCleanup();
    }
    window.__dgMobileControlsCleanup = null;

    var root = document.documentElement;
    var mobileMedia = window.matchMedia(MOBILE_QUERY);
    var reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    var header = document.querySelector('store-header');
    var stickyHeaderEnabled = Boolean(header && header.hasAttribute('data-is-sticky'));
    var search = document.querySelector('.header__search');
    var searchContent = search && search.querySelector('.search');
    var mobileCats = document.querySelector('.dg-mobile-cats');
    var collectionToolbar = document.querySelector(
      '.cc-collection-products products-toolbar.products-toolbar'
    );
    var collectionToolbarOrigin = collectionToolbar && collectionToolbar.parentElement;

  // Une grille de header sans formulaire peut contenir un placeholder vide.
    if (search && !searchContent) search = null;
  // Si le réglage natif « recherche minimisée » est activé, StoreHeader garde
  // la propriété de cette ligne et de son bouton d'ouverture. On ne la rend
  // jamais inert depuis ce contrôleur directionnel.
    if (
      search
      && header
      && header.hasAttribute('data-is-search-minimised')
      && search.classList.contains('header__search--collapsible-mob')
    ) {
      search = null;
      searchContent = null;
    }
    if (!search && !mobileCats && !collectionToolbar) return;

  var controlsHidden = false;
  var catsHidden = false;
  var directionAccum = 0;
  var lastY = Math.max(0, window.scrollY);
  var lastWidth = Math.round(document.documentElement.clientWidth || window.innerWidth);
  var toolbarHeight = collectionToolbar ? collectionToolbar.offsetHeight : 0;
  var ticking = false;
  var measureFrame = 0;
  var measureRetryTimer = 0;
  var transitionTimer = 0;
  var settleUntil = 0;
  var lastInputDirection = 0;
  var lastInputTime = 0;
  var lastTouchY = null;
    var resizeObserver = null;
    var geometry = null;
    var active = true;

  function isMobile() {
    return mobileMedia.matches;
  }

  function finiteNumber(value, fallback) {
    var number = parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function setElementUnavailable(element, unavailable) {
    if (!element) return;
    if (unavailable) {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    } else {
      element.removeAttribute('inert');
      element.removeAttribute('aria-hidden');
    }
  }

  function measureSearchHeight() {
    if (!search || !searchContent) return 0;

    var contentRect = searchContent.getBoundingClientRect();
    var contentStyle = window.getComputedStyle(searchContent);
    var searchStyle = window.getComputedStyle(search);

    return Math.max(0,
      contentRect.height
      + finiteNumber(contentStyle.marginTop, 0)
      + finiteNumber(contentStyle.marginBottom, 0)
      + finiteNumber(searchStyle.paddingTop, 0)
      + finiteNumber(searchStyle.paddingBottom, 0)
      + finiteNumber(searchStyle.borderTopWidth, 0)
      + finiteNumber(searchStyle.borderBottomWidth, 0)
    );
  }

  function measureMobileCatsHeight() {
    if (!mobileCats) return 0;

    var catsStyle = window.getComputedStyle(mobileCats);
    return Math.max(0,
      mobileCats.scrollHeight
      + finiteNumber(catsStyle.borderTopWidth, 0)
      + finiteNumber(catsStyle.borderBottomWidth, 0)
    );
  }

  function getStableHeaderTop(headerRect) {
    if (!header) return { top: 0, isStuck: true };

    // Sans header sticky, le formulaire sort naturellement du viewport et la
    // toolbar doit simplement se fixer en haut, sans suivre une coordonnée de
    // header qui devient négative au fil du scroll.
    if (!stickyHeaderEnabled) return { top: 0, isStuck: true };

    var stickyContainer = header.closest('.cc-header');
    if (stickyContainer) {
      var stickyStyle = window.getComputedStyle(stickyContainer);
      if (stickyStyle.position === 'sticky') {
        var stickyTop = finiteNumber(stickyStyle.top, headerRect.top);
        var containerTop = stickyContainer.getBoundingClientRect().top;
        var isStuck = window.scrollY > 0 && containerTop <= stickyTop + 0.5;
        return { top: isStuck ? stickyTop : headerRect.top, isStuck: isStuck };
      }
    }

    // À la restauration d'un scroll profond, StoreHeader rend le parent sticky
    // au tick suivant. Ne publions pas entre-temps une coordonnée très négative.
    if (window.scrollY > 0 && headerRect.bottom <= 0) return null;
    return { top: headerRect.top, isStuck: false };
  }

  function publishHeaderTarget() {
    if (!geometry || !stickyHeaderEnabled) return;

    var targetHeight = controlsHidden ? geometry.compactHeight : geometry.expandedHeight;
    var targetEnd = controlsHidden ? geometry.compactEnd : geometry.expandedEnd;
    root.style.setProperty('--header-height', targetHeight.toFixed(1) + 'px');
    root.style.setProperty('--header-end', targetEnd.toFixed(1) + 'px');
    root.style.setProperty('--header-end-padded', (targetEnd + 20).toFixed(1) + 'px');
  }

  function toolbarHasPassedOrigin(currentY) {
    if (!collectionToolbar || !collectionToolbarOrigin || !geometry) return false;

    var scrollTop = typeof currentY === 'number' ? currentY : Math.max(0, window.scrollY);
    // Origine document normalisée à la mise en page compacte : aucune lecture
    // de layout n'est nécessaire pendant le scroll ou la transition.
    return scrollTop + geometry.compactEnd
      >= geometry.toolbarOriginCompactY + toolbarHeight - 0.5;
  }

  function syncToolbar(toolbarPassed) {
    if (!collectionToolbar) return;

    var shouldHide = isMobile() && controlsHidden && toolbarPassed;
    collectionToolbar.classList.toggle('products-toolbar--out', shouldHide);
    setElementUnavailable(collectionToolbar, shouldHide);
  }

  function measureGeometry() {
    measureFrame = 0;
    if (!isMobile() || root.classList.contains('dg-mobile-controls--transitioning')) return false;
    if (!header) return true;

    // Toutes les lectures sont regroupées avant la moindre écriture de style.
    var headerRect = header.getBoundingClientRect();
    var headerPosition = getStableHeaderTop(headerRect);
    if (headerPosition === null) return false;

    var searchHeight = measureSearchHeight();
    var catsHeight = measureMobileCatsHeight();
    var measuredToolbarHeight = collectionToolbar ? collectionToolbar.offsetHeight : 0;
    var originDocumentY = collectionToolbarOrigin
      ? collectionToolbarOrigin.getBoundingClientRect().top + window.scrollY
      : null;
    var compactHeight = Math.max(
      0,
      headerRect.height - (controlsHidden ? 0 : searchHeight)
    );
    var compactEnd = stickyHeaderEnabled
      ? headerPosition.top + compactHeight
      : 0;
    var expandedEnd = stickyHeaderEnabled
      ? compactEnd + searchHeight
      : 0;

    toolbarHeight = measuredToolbarHeight;
    geometry = {
      searchHeight: searchHeight,
      compactHeight: compactHeight,
      expandedHeight: compactHeight + searchHeight,
      compactEnd: compactEnd,
      expandedEnd: expandedEnd,
      headerIsStuck: headerPosition.isStuck,
      toolbarOriginCompactY: originDocumentY === null
        ? Number.POSITIVE_INFINITY
        : originDocumentY
          - (controlsHidden ? 0 : searchHeight)
          - (catsHidden ? 0 : catsHeight)
    };

    root.style.setProperty('--dg-mobile-search-height', searchHeight.toFixed(1) + 'px');
    root.style.setProperty('--dg-mobile-header-compact-end', geometry.compactEnd.toFixed(1) + 'px');
    root.style.setProperty('--dg-mobile-header-expanded-end', geometry.expandedEnd.toFixed(1) + 'px');
    publishHeaderTarget();
    syncToolbar(toolbarHasPassedOrigin());
    return true;
  }

  function scheduleMeasure(retryCount) {
    var retries = typeof retryCount === 'number' ? retryCount : 0;
    if (!active) return;
    if (measureFrame) window.cancelAnimationFrame(measureFrame);
    if (measureRetryTimer) {
      window.clearTimeout(measureRetryTimer);
      measureRetryTimer = 0;
    }

    // Le fichier peut déjà être présent dans le cache lors d'un passage vers
    // le bureau. Ne programme aucune frame ni les quatre retries de géométrie
    // hors breakpoint mobile ; onMediaChange relancera la mesure au retour.
    if (!isMobile()) return;

    measureFrame = window.requestAnimationFrame(function () {
      if (!measureGeometry() && retries < 4) {
        measureRetryTimer = window.setTimeout(function () {
          scheduleMeasure(retries + 1);
        }, 40);
      }
    });
  }

  function endTransition() {
    if (transitionTimer) {
      window.clearTimeout(transitionTimer);
      transitionTimer = 0;
    }
    root.classList.remove('dg-mobile-controls--transitioning');
    settleUntil = 0;
    scheduleMeasure();
  }

  function beginTransition() {
    if (transitionTimer) window.clearTimeout(transitionTimer);
    root.classList.add('dg-mobile-controls--transitioning');
    // La garde reste active jusqu'au vrai transitionend. Un touch/wheel dans
    // l'autre sens peut néanmoins inverser immédiatement le mouvement.
    settleUntil = Number.POSITIVE_INFINITY;

    transitionTimer = window.setTimeout(
      endTransition,
      reducedMotionMedia.matches ? 0 : TRANSITION_MS + 40
    );
  }

  function interactionNeedsControls() {
    var body = document.body;
    if (!body) return false;

    if (
      body.classList.contains('overlay-predictive-search')
      || body.classList.contains('fixed')
      || body.classList.contains('overflow-hidden')
    ) {
      return true;
    }

    if (document.querySelector('facet-filters[open][aria-hidden="false"]')) return true;
    return Boolean(
      (search && search.contains(document.activeElement))
      || (collectionToolbar && collectionToolbar.contains(document.activeElement))
    );
  }

  function setControlsHidden(shouldHide, toolbarPassed, immediate) {
    if (!isMobile() || !stickyHeaderEnabled) shouldHide = false;
    if (shouldHide && interactionNeedsControls()) shouldHide = false;

    if (shouldHide === controlsHidden) {
      syncToolbar(toolbarPassed);
      return;
    }

    controlsHidden = shouldHide;
    directionAccum = 0;

    if (immediate) {
      settleUntil = 0;
      root.classList.remove('dg-mobile-controls--transitioning');
    } else {
      beginTransition();
    }

    // Ces mutations ont lieu dans la même tâche : le navigateur ne peut pas
    // peindre un état où la recherche et la toolbar se contredisent.
    root.classList.toggle('dg-mobile-controls--hidden', shouldHide);
    if (search) search.classList.toggle('header__search--out', shouldHide);
    setElementUnavailable(search, shouldHide);
    syncToolbar(toolbarPassed);
    publishHeaderTarget();
  }

  function syncMobileCats(currentY, immediate) {
    if (!mobileCats) return;

    var shouldHide = catsHidden
      ? currentY > CATS_SHOW_AT
      : currentY > CATS_HIDE_AT;

    if (
      !isMobile()
      || mobileCats.contains(document.activeElement)
    ) shouldHide = false;
    if (shouldHide === catsHidden && !immediate) return;

    catsHidden = shouldHide;
    mobileCats.classList.toggle('is-hidden', shouldHide);
    setElementUnavailable(mobileCats, shouldHide);
  }

  function addDirectionalDelta(delta) {
    if ((delta > 0 && directionAccum < 0) || (delta < 0 && directionAccum > 0)) {
      directionAccum = 0;
    }
    directionAccum += delta;
  }

  function recordInputDirection(delta) {
    if (Math.abs(delta) < 1) return;
    lastInputDirection = delta > 0 ? 1 : -1;
    lastInputTime = performance.now();
  }

  function hasRecentInput(direction) {
    return lastInputDirection === direction && performance.now() - lastInputTime < 180;
  }

  function isSyntheticTransitionReversal(delta) {
    if (!delta || performance.now() >= settleUntil) return false;

    var direction = delta > 0 ? 1 : -1;
    var transitionDirection = controlsHidden ? 1 : -1;
    return direction !== transitionDirection && !hasRecentInput(direction);
  }

  function update() {
    ticking = false;
    if (!active) return;

    var currentY = Math.max(0, window.scrollY);
    var delta = currentY - lastY;
    lastY = currentY;

    if (!isMobile()) {
      resetForViewport();
      return;
    }

    syncMobileCats(currentY, false);
    if (geometry && !geometry.headerIsStuck && currentY > TOP_REVEAL) scheduleMeasure();
    var toolbarPassed = toolbarHasPassedOrigin(currentY);

    if (!stickyHeaderEnabled) {
      setControlsHidden(false, toolbarPassed, true);
      directionAccum = 0;
      return;
    }

    if (interactionNeedsControls() || currentY <= TOP_REVEAL) {
      setControlsHidden(false, toolbarPassed, false);
      directionAccum = 0;
      return;
    }

    // Le reflow de la ligne de recherche peut générer un scroll inverse sans
    // geste utilisateur. La garde dure jusqu'à la fin de transition, mais un
    // touch/wheel réel peut toujours l'inverser immédiatement.
    if (isSyntheticTransitionReversal(delta)) {
      directionAccum = 0;
      syncToolbar(toolbarPassed);
      return;
    }

    if (Math.abs(delta) < 0.5) {
      syncToolbar(toolbarPassed);
      return;
    }

    addDirectionalDelta(delta);

    if (!controlsHidden && directionAccum >= HIDE_DISTANCE) {
      setControlsHidden(true, toolbarPassed, false);
    } else if (controlsHidden && directionAccum <= -SHOW_DISTANCE) {
      setControlsHidden(false, toolbarPassed, false);
    } else {
      syncToolbar(toolbarPassed);
    }
  }

  function onScroll() {
    if (!isMobile() || ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function revealForInteraction() {
    if (!isMobile()) return;
    var toolbarPassed = toolbarHasPassedOrigin();
    setControlsHidden(false, toolbarPassed, false);
    lastY = Math.max(0, window.scrollY);
    directionAccum = 0;
  }

  function onFocusIn(event) {
    if (mobileCats && mobileCats.contains(event.target)) {
      syncMobileCats(Math.max(0, window.scrollY), true);
    }

    if (
      (search && search.contains(event.target))
      || (collectionToolbar && collectionToolbar.contains(event.target))
    ) {
      revealForInteraction();
    }
  }

  function onWheel(event) {
    if (!isMobile()) return;
    recordInputDirection(event.deltaY);
  }

  function onTouchStart(event) {
    if (!isMobile()) return;
    lastTouchY = event.touches && event.touches.length ? event.touches[0].clientY : null;
  }

  function onTouchMove(event) {
    if (!isMobile()) return;
    if (lastTouchY === null || !event.touches || !event.touches.length) return;
    var currentTouchY = event.touches[0].clientY;
    recordInputDirection(lastTouchY - currentTouchY);
    lastTouchY = currentTouchY;
  }

  function resetForViewport() {
    lastY = Math.max(0, window.scrollY);
    directionAccum = 0;
    settleUntil = 0;

    if (transitionTimer) {
      window.clearTimeout(transitionTimer);
      transitionTimer = 0;
    }

    root.classList.remove(
      'dg-mobile-controls--hidden',
      'dg-mobile-controls--transitioning'
    );
    controlsHidden = false;

    if (search) search.classList.remove('header__search--out');
    if (collectionToolbar) collectionToolbar.classList.remove('products-toolbar--out');
    setElementUnavailable(search, false);
    setElementUnavailable(collectionToolbar, false);
    syncMobileCats(lastY, true);

    if (isMobile()) {
      scheduleMeasure();
    } else if (
      stickyHeaderEnabled
      && header
      && typeof header.setHeaderEnd === 'function'
    ) {
      window.requestAnimationFrame(function () {
        if (active) header.setHeaderEnd();
      });
    }
  }

  function onResize() {
    var currentWidth = Math.round(document.documentElement.clientWidth || window.innerWidth);

    // La barre d'adresse mobile ne modifie que la hauteur : elle ne doit jamais
    // réinitialiser un des deux éléments indépendamment de l'autre.
    if (currentWidth === lastWidth) return;
    lastWidth = currentWidth;
    lastY = Math.max(0, window.scrollY);
    directionAccum = 0;

    if (isMobile()) scheduleMeasure();
  }

  function onMediaChange() {
    resetForViewport();
  }

  function onPageShow() {
    resetForViewport();
  }

  function onSearchTransitionEnd(event) {
    if (event.target === search && event.propertyName === 'max-height') endTransition();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('wheel', onWheel, { passive: true });
  window.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('touchmove', onTouchMove, { passive: true });
  window.addEventListener('load', scheduleMeasure);
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('on:facet-filters:before-open', revealForInteraction);
  document.addEventListener('on:facet-filters:updated', scheduleMeasure);
  if (search) search.addEventListener('transitionend', onSearchTransitionEnd);

  if (typeof mobileMedia.addEventListener === 'function') {
    mobileMedia.addEventListener('change', onMediaChange);
  } else {
    mobileMedia.addListener(onMediaChange);
  }

  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(function () {
      if (isMobile() && !root.classList.contains('dg-mobile-controls--transitioning')) {
        scheduleMeasure();
      }
    });
    if (searchContent) resizeObserver.observe(searchContent);
    if (collectionToolbar) resizeObserver.observe(collectionToolbar);
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleMeasure);
  }

  window.__dgMobileControlsCleanup = function cleanupMobileControls() {
    active = false;
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('load', scheduleMeasure);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('on:facet-filters:before-open', revealForInteraction);
    document.removeEventListener('on:facet-filters:updated', scheduleMeasure);
    if (search) search.removeEventListener('transitionend', onSearchTransitionEnd);

    if (typeof mobileMedia.removeEventListener === 'function') {
      mobileMedia.removeEventListener('change', onMediaChange);
    } else {
      mobileMedia.removeListener(onMediaChange);
    }

    if (resizeObserver) resizeObserver.disconnect();
    if (measureFrame) window.cancelAnimationFrame(measureFrame);
    if (measureRetryTimer) window.clearTimeout(measureRetryTimer);
    if (transitionTimer) window.clearTimeout(transitionTimer);

    root.classList.remove(
      'dg-mobile-controls--hidden',
      'dg-mobile-controls--transitioning'
    );
    if (search) search.classList.remove('header__search--out');
    if (collectionToolbar) collectionToolbar.classList.remove('products-toolbar--out');
    if (mobileCats) mobileCats.classList.remove('is-hidden');
    setElementUnavailable(search, false);
    setElementUnavailable(collectionToolbar, false);
    setElementUnavailable(mobileCats, false);

    if (window.__dgMobileControlsCleanup === cleanupMobileControls) {
      window.__dgMobileControlsCleanup = null;
    }
  };

  syncMobileCats(lastY, true);
  resetForViewport();
  // StoreHeader rend le conteneur sticky dans un setTimeout : deux frames
  // garantissent aussi une géométrie correcte après restauration du scroll.
  window.requestAnimationFrame(function () {
    scheduleMeasure();
  });
  }

  window.__dgInitMobileControls = initMobileControls;
  if (!window.__dgMobileControlsSectionLoadHandler) {
    window.__dgMobileControlsSectionLoadHandler = function (event) {
      if (
        sectionContainsManagedControls(event.target)
        && typeof window.__dgInitMobileControls === 'function'
      ) {
        window.__dgInitMobileControls();
      }
    };
    document.addEventListener(
      'shopify:section:load',
      window.__dgMobileControlsSectionLoadHandler
    );
  }

  initMobileControls();
  if (
    window.__dgMobileControlsAssetGate
    && typeof window.__dgMobileControlsAssetGate.didLoad === 'function'
  ) {
    window.__dgMobileControlsAssetGate.didLoad();
  }
})();
